(() => {
  const SECTION_SELECTOR = "[data-calinfi-odun-motion]";
  const SEQUENCE_SELECTOR = "[data-calinfi-odun-sequence]";
  const FRAME_DATA_SELECTOR = "[data-calinfi-odun-sequence-frames]";
  const MAX_DEVICE_PIXEL_RATIO = 2;
  const DRAG_PIXELS_PER_FRAME = 8;
  const PRELOAD_BATCH_SIZE = 6;
  const PRELOAD_INTERVAL_MS = 90;

  const parseFrameUrls = (element) => {
    const frameData = element.querySelector(FRAME_DATA_SELECTOR);

    if (!frameData) return [];

    try {
      const urls = JSON.parse(frameData.textContent);

      if (!Array.isArray(urls)) return [];

      return urls.filter((url) => typeof url === "string" && url.length > 0);
    } catch (error) {
      return [];
    }
  };

  const normalizeFrameIndex = (index, frameCount) => {
    return ((index % frameCount) + frameCount) % frameCount;
  };

  const createImageSequence = (section, sequence, prefersReducedMotion, shouldPauseForReducedMotion) => {
    const canvas = sequence.querySelector("canvas");
    const context = canvas ? canvas.getContext("2d") : null;
    const frameUrls = parseFrameUrls(sequence);

    if (!canvas || !context || !frameUrls.length) return null;

    const frameRate = Math.max(1, Number(sequence.dataset.frameRate) || 24);
    const frameDuration = 1000 / frameRate;
    const shouldAutoplay = sequence.dataset.autoplay !== "false";
    const images = new Array(frameUrls.length);
    const imagePromises = new Array(frameUrls.length);

    let frameIndex = 0;
    let animationFrame = null;
    let lastFrameTime = 0;
    let isVisible = false;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartFrame = 0;
    let preloadCursor = 1;
    let preloadTimer = null;

    const loadFrame = (index) => {
      const normalizedIndex = normalizeFrameIndex(index, frameUrls.length);

      if (images[normalizedIndex]) {
        return Promise.resolve(images[normalizedIndex]);
      }

      if (imagePromises[normalizedIndex]) {
        return imagePromises[normalizedIndex];
      }

      imagePromises[normalizedIndex] = new Promise((resolve, reject) => {
        const image = new Image();

        image.decoding = "async";
        image.onload = () => {
          images[normalizedIndex] = image;
          resolve(image);
        };
        image.onerror = reject;
        image.src = frameUrls[normalizedIndex];
      });

      return imagePromises[normalizedIndex];
    };

    const getCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      const cssWidth = Math.max(1, rect.width);
      const cssHeight = Math.max(1, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
      const width = Math.round(cssWidth * dpr);
      const height = Math.round(cssHeight * dpr);

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      return { cssWidth, cssHeight };
    };

    const drawImageContain = (image) => {
      const { cssWidth, cssHeight } = getCanvasSize();
      const imageWidth = image.naturalWidth || image.width;
      const imageHeight = image.naturalHeight || image.height;

      if (!imageWidth || !imageHeight) return;

      const scale = Math.min(cssWidth / imageWidth, cssHeight / imageHeight);
      const drawWidth = imageWidth * scale;
      const drawHeight = imageHeight * scale;
      const drawX = (cssWidth - drawWidth) / 2;
      const drawY = (cssHeight - drawHeight) / 2;

      context.clearRect(0, 0, cssWidth, cssHeight);
      context.drawImage(image, drawX, drawY, drawWidth, drawHeight);
      sequence.classList.add("is-ready");
    };

    const drawFrame = (index) => {
      const requestedIndex = normalizeFrameIndex(index, frameUrls.length);

      frameIndex = requestedIndex;
      loadFrame(requestedIndex)
        .then((image) => {
          if (frameIndex === requestedIndex) {
            drawImageContain(image);
          }
        })
        .catch(() => {});
    };

    const preloadAround = (index) => {
      for (let offset = 1; offset <= PRELOAD_BATCH_SIZE; offset += 1) {
        loadFrame(index + offset).catch(() => {});
      }
    };

    const schedulePreload = () => {
      if (preloadTimer || preloadCursor >= frameUrls.length || !isVisible) return;

      preloadTimer = window.setTimeout(() => {
        preloadTimer = null;

        for (let count = 0; count < PRELOAD_BATCH_SIZE && preloadCursor < frameUrls.length; count += 1) {
          loadFrame(preloadCursor).catch(() => {});
          preloadCursor += 1;
        }

        schedulePreload();
      }, PRELOAD_INTERVAL_MS);
    };

    const stopLoop = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }

      lastFrameTime = 0;
    };

    const canAutoplay = () => {
      return (
        shouldAutoplay &&
        isVisible &&
        !isDragging &&
        !(shouldPauseForReducedMotion && prefersReducedMotion.matches)
      );
    };

    const tick = (timestamp) => {
      if (!canAutoplay()) {
        stopLoop();
        return;
      }

      if (!lastFrameTime) {
        lastFrameTime = timestamp;
      }

      if (timestamp - lastFrameTime >= frameDuration) {
        drawFrame(frameIndex + 1);
        preloadAround(frameIndex);
        lastFrameTime = timestamp;
      }

      animationFrame = window.requestAnimationFrame(tick);
    };

    const refresh = () => {
      if (shouldPauseForReducedMotion && prefersReducedMotion.matches) {
        stopLoop();
        drawFrame(0);
        return;
      }

      if (!canAutoplay()) {
        stopLoop();
        return;
      }

      schedulePreload();

      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(tick);
      }
    };

    const handlePointerDown = (event) => {
      if (event.button && event.button !== 0) return;

      isDragging = true;
      dragStartX = event.clientX;
      dragStartFrame = frameIndex;
      sequence.classList.add("is-dragging");

      if (typeof sequence.setPointerCapture === "function") {
        sequence.setPointerCapture(event.pointerId);
      }

      refresh();
    };

    const handlePointerMove = (event) => {
      if (!isDragging) return;

      event.preventDefault();

      const frameDelta = Math.round((event.clientX - dragStartX) / DRAG_PIXELS_PER_FRAME);
      drawFrame(dragStartFrame + frameDelta);
      preloadAround(frameIndex);
    };

    const handlePointerEnd = (event) => {
      if (!isDragging) return;

      isDragging = false;
      sequence.classList.remove("is-dragging");

      if (typeof sequence.releasePointerCapture === "function") {
        try {
          sequence.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture may already be released by the browser.
        }
      }

      refresh();
    };

    sequence.addEventListener("pointerdown", handlePointerDown);
    sequence.addEventListener("pointermove", handlePointerMove);
    sequence.addEventListener("pointerup", handlePointerEnd);
    sequence.addEventListener("pointercancel", handlePointerEnd);
    sequence.addEventListener("lostpointercapture", handlePointerEnd);

    if ("ResizeObserver" in window) {
      const resizeObserver = new ResizeObserver(() => drawFrame(frameIndex));
      resizeObserver.observe(sequence);
    } else {
      window.addEventListener("resize", () => drawFrame(frameIndex), { passive: true });
    }

    drawFrame(0);
    preloadAround(0);

    return {
      setVisible(visible) {
        isVisible = visible;

        if (!visible && preloadTimer) {
          window.clearTimeout(preloadTimer);
          preloadTimer = null;
        }

        refresh();
      },
      syncMotionPreference() {
        refresh();
      },
    };
  };

  const initSection = (section) => {
    if (section.dataset.odunMotionInitialized === "true") return;
    section.dataset.odunMotionInitialized = "true";

    const videos = Array.from(section.querySelectorAll("video"));
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const shouldPauseForReducedMotion = section.dataset.reducedMotionPoster === "true";
    const sequences = Array.from(section.querySelectorAll(SEQUENCE_SELECTOR))
      .map((sequence) => createImageSequence(section, sequence, prefersReducedMotion, shouldPauseForReducedMotion))
      .filter(Boolean);

    if (!videos.length && !sequences.length) return;

    let sectionIsVisible = false;

    const pauseAll = () => {
      videos.forEach((video) => {
        video.pause();
      });

      sequences.forEach((sequence) => sequence.setVisible(false));
    };

    const playAll = () => {
      const isReduced = shouldPauseForReducedMotion && prefersReducedMotion.matches;

      section.classList.toggle("is-reduced-motion", isReduced);

      if (isReduced) {
        videos.forEach((video) => {
          video.pause();
        });
      } else {
        videos.forEach((video) => {
          video.muted = true;
          video.play().catch(() => {});
        });
      }

      sequences.forEach((sequence) => sequence.setVisible(true));
    };

    const setVisible = (visible) => {
      sectionIsVisible = visible;

      if (visible) {
        playAll();
      } else {
        pauseAll();
      }
    };

    const syncMotionPreference = () => {
      sequences.forEach((sequence) => sequence.syncMotionPreference());

      if (sectionIsVisible) {
        playAll();
      } else {
        pauseAll();
      }
    };

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setVisible(entry.isIntersecting);
          });
        },
        { threshold: 0.2 }
      );

      observer.observe(section);
    } else {
      setVisible(true);
    }

    syncMotionPreference();

    if (typeof prefersReducedMotion.addEventListener === "function") {
      prefersReducedMotion.addEventListener("change", syncMotionPreference);
    } else if (typeof prefersReducedMotion.addListener === "function") {
      prefersReducedMotion.addListener(syncMotionPreference);
    }
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
